import Component from "../../component";
import {isElementVisibleInViewport} from "../../helpers/element_in_viewport";
import {CollapserToggle, CollapserToggleConfig} from "./collapser_toggle";
import {CollapserBody, CollapserBodyConfig} from "./collapser_body";
import {CollapserHeader, CollapserHeaderConfig} from "./header";

export const CollapserConfig = {
    signature: 'Collapser',
    baseHtmlClass: 'collapser',
    selector: '.collapser',

    datasetConfigKey: 'collapser_config_data',
    openDatasetConfigKey: 'open',
    collapsableDatasetConfigKey: 'collapsable',

    collapsableClassModifier: 'collapsable',
    uncollapsableClassModifier: 'uncollapsable',

    openClassModifier: 'open',
    closedClassModifier: 'closed',
};

export class Collapser extends Component {

    static applyToAll(){
        const elements = document.querySelectorAll(CollapserConfig.selector);
        elements.forEach((element) => {
            if (!Collapser.isElementComponentized(element, CollapserConfig.signature)) new Collapser(element);
        });
    }

    constructor(id_or_element) {

        super({
            element: id_or_element,
            config: CollapserConfig
        });

        this.setDatasetConfig(CollapserConfig.datasetConfigKey);
        this.open = this.getDefaultOpenState(); // Is it open?
        this.collapsable = this.getDefaultCollapsableState(); // Is it able to be collapsed?

        // Get our components…
        this.toggle = getToggleComponent(this);
        this.header = getHeaderComponent(this);
        this.body = getBodyComponent(this);

        this.transitionDuration = 500;

        this.openHeight = null;
        this.closedHeight = null;
        this.height = null;

        this.renderInitial();

        this.render();

        if (this.isOpen()) this.unlockAndCalcHeights();
    }

    getDefaultOpenState() {
        const open = this.getDatasetConfigValue(CollapserConfig.openDatasetConfigKey);
        return open ?? true;
    }

    getDefaultCollapsableState() {
        const collapsable = this.getDatasetConfigValue(CollapserConfig.collapsableDatasetConfigKey);
        return collapsable ?? false;
    }

    renderInitial() {
        this.addHtmlClassState('inited');
    }

    renderHeight() {
        if (this.isCollapsable()) {
            this.element.style.height = (this.height == null) ? null : `${this.height}px`;
        } else {
            this.element.style.height = null;
        }
    }

    render() {
        this.element.style['transitionDuration'] = `${this.transitionDuration}ms`;

        this.calculateHeights();
        this.renderHeight();
        this.swapHtmlClassStates(this.isCollapsable(), CollapserConfig.collapsableClassModifier, CollapserConfig.uncollapsableClassModifier);
        this.swapHtmlClassStates(this.isOpen(), CollapserConfig.openClassModifier, CollapserConfig.closedClassModifier);

        // Render the sub-components…
        this.toggle.render();
        this.header.render();
        this.body.render();
    }

    isCollapsable() {
        return !!this.collapsable;
    }

    isOpen() {
        return !!this.open;
    }

    scrollIntoView() {
        const scrollToElement = this.element;
        if (scrollToElement && !isElementVisibleInViewport(scrollToElement)) {
            if (this.isOpen()) scrollToElement.scrollIntoView({block: 'end', behavior: 'smooth'});
        }
    }

    toggleClosed() {
        this.lockHeight(); // Before closing we lock the height to an absolute value (it might have been unlocked to `auto`).
        this.height = this.closedHeight;
        this.open = false;
    }

    toggleOpen() {
        // After we open we ensure the options are in the viewport.
        setTimeout(this.scrollIntoView.bind(this), 300); // This must happen after the height change CSS transition has completed.

        // After we open we remove the absolute height value (set it to `auto`) to deal with potential inaccurately computed height.
        this.unlockAndCalcHeights();

        this.height = this.openHeight;
        this.open = true;
    }

    toggleCollapse() {
        if (this.isOpen()) { // Will be closing…
            this.toggleClosed();
        } else { // Will be opening…
            this.toggleOpen();
        }

        // Rendering after a short delay seems necessary!
        // Otherwise the browser doesn't "notice" the change in height when locking the height just prior to setting it to the closing height.
        // 50ms seems to work fine.
        setTimeout(this.render.bind(this), 50);
    }

    // Remove any absolutely calculated height and set the element's height to 'auto'.
    unlockHeight() {
        if (!this.isOpen()) return;
        this.height = null;
        this.renderHeight();
    }

    lockHeight() {
        if (!this.isOpen()) return;
        this.height = this.getOpenHeight();
        this.renderHeight();
    }

    getElementHeight() {
        return this.element.offsetHeight;
    }

    getOpenHeight() {
        return this.getElementHeight();
    }

    getClosedHeight() {
        return this.getHeaderHeight();
    }

    getHeaderHeight() {
        return (!!this.header?.element) ? this.header.element.offsetHeight : 0;
    }

    unlockAndCalcHeights() {
        // A delay is required!
        setTimeout(() => {
            this.unlockHeight();
            // Now we've unlocked the height the elements should size to their contents.
            // So now's a good time to update our heights to "correct" values!
            this.calculateHeights();
        }, this.transitionDuration); // This will happen after the height change CSS transition has completed.
    }

    calculateHeights() {
        if (this.isUnlocked()) this.openHeight = this.getOpenHeight(); // Only calc the open height when an absolute value is not applied.
        this.closedHeight = this.getClosedHeight(); // Get the closed height at any time.
        this.height = this.isOpen() ? this.openHeight : this.closedHeight;
    }

    isUnlocked() {
        return this.element.style.height === 'auto' || this.element.style.height === "" || this.element.style.height === null;
    }


}

function getToggleComponent(collapserComponent) {
    const element = collapserComponent.element.querySelector(CollapserToggleConfig.selector);
    if (!element) return;
    return new CollapserToggle(element, collapserComponent);
}

function getHeaderComponent(collapserComponent) {
    const element = collapserComponent.element.querySelector(CollapserHeaderConfig.selector);
    if (!element) return;
    return new CollapserHeader(element, collapserComponent);
}

function getBodyComponent(collapserComponent) {
    const element = collapserComponent.element.querySelector(CollapserBodyConfig.selector);
    if (!element) return;
    return new CollapserBody(element, collapserComponent);
}
